<template>
  <v-container>
    <v-row class="tw-mt-4">
      <v-col cols="5">
        <label>From: </label>
        <input type="month" v-model="fromMonth" />
      </v-col>
      <v-col cols="5">
        <label>To: </label>
        <input type="month" v-model="toMonth" />
      </v-col>
      <v-col cols="2">
        <v-btn @click="searchTrend" color="primary" elevation="2">Search</v-btn>
      </v-col>
    </v-row>
    <v-row class="overflow-x-auto overflow-y-hidden">
      <vue-apex-charts
        v-if="series.length > 0"
        :options="chartOptions"
        :series="series"
        :height="600"
      />
    </v-row>
  </v-container>
</template>

<style>
.apexcharts-canvas {
  min-width: 200px;
}
@media (min-width: 600px) {
  .apexcharts-canvas {
    min-width: 400px;
    min-height: 500px;
  }
}
@media (min-width: 800px) {
  .apexcharts-canvas {
    min-width: 600px;
    min-height: 500px;
  }
}
@media (min-width: 1000px) {
  .apexcharts-canvas {
    min-width: 800px;
  }
}
@media (min-width: 1280px) {
  .apexcharts-canvas {
    min-width: 1000px;
  }
}
</style>

<script lang="ts">
import { defineComponent, ref } from "vue";
import { loadQuery, queryDataFromDate } from "../firebase/manageData";
import {
  getLastMonth,
  getThisMonth,
  getFirstDateOfMonth,
  getLastDateOfMonth,
} from "../util/date-util";
import VueApexCharts from "vue3-apexcharts";
import { Transaction } from "../data-types";

export default defineComponent({
  name: "TrendView",
  components: {
    VueApexCharts,
  },
  setup() {
    const isSignedIn = ref<Boolean>(false);
    const fromMonth = ref<string>(getLastMonth());
    const toMonth = ref<string>(getThisMonth());

    const chartOptions = ref<any>({});
    const series = ref<{ name: string; data: number[] }[]>([]);

    const searchTrend = async () => {
      series.value = [];
      const xaxisCategories: string[] = [];
      const from = getFirstDateOfMonth(new Date(fromMonth.value + "-02"));
      const to = getLastDateOfMonth(new Date(toMonth.value + "-02"));
      const query = queryDataFromDate(from, to);
      let i = Number(from.split("-")[0]);
      let j = Number(from.split("-")[1]);
      while (new Date(String(i) + "-" + String(j)) <= new Date(to)) {
        let yearMonth = String(i) + "-" + String(j);
        xaxisCategories.push(yearMonth);
        i = j < 12 ? i : i + 1;
        j = j < 12 ? j + 1 : 1;
      }
      const loadData = await loadQuery(query);
      loadData.tableData = loadData.tableData.filter(
        (transaction: Transaction) =>
          !["Income", "Reimbursement", "Payment", "Transfer"].includes(
            transaction.category
          ) && transaction.amount > 0
      );

      loadData.tableData.forEach((transaction) => {
        if (!series.value.map((e) => e.name).includes(transaction.category)) {
          series.value.push({
            name: transaction.category,
            data: Array(xaxisCategories.length).fill(0),
          });
        }
        let index = series.value.findIndex(
          (graph: any) => graph.name === transaction.category
        );
        let jndex = xaxisCategories.findIndex(
          (e: string) =>
            e === String(transaction.year) + "-" + String(transaction.month)
        );
        series.value[index].data[jndex] += Math.round(transaction.amount);
      });

      chartOptions.value = {
        chart: {
          type: "bar",
          stacked: true,
          toobar: {
            show: true,
          },
          zoom: {
            enabled: false,
          },
        },
        legend: {
          position: "right",
          offsetY: 20,
        },
        dataLabels: {
          enabled: true,
          formatter: (val: any, opts: any) => {
            const sum = opts?.globals?.series
              .map((e: number[]) => e[opts?.dataPointIndex])
              .reduce((a: number, b: number) => a + b, 0);
            const percentage = Math.round((100 * val) / sum);
            return `$${val} ${percentage < 100 ? `(${percentage}%)` : ""}`;
          },
          style: {
            colors: ["black"],
          },
        },
        xaxis: {
          categories: xaxisCategories,
        },

        responsive: [
          {
            breakpoint: 800,
            options: {
              legend: {
                position: "bottom",
              },
              dataLabels: {
                enabled: false,
              },
            },
          },
        ],
      };
    };

    return {
      isSignedIn,
      fromMonth,
      toMonth,
      searchTrend,
      chartOptions,
      series,
    };
  },
});
</script>
